/* 最小 ZIP 讀取器。DOCX 其實就是一個 zip，而現代瀏覽器與 Node 都內建 DecompressionStream，
   所以不需要引入任何依賴（少一個依賴＝少一個日後要維護的東西）。

   只做「讀取特定幾個檔案」這一件事：解析中央目錄（權威來源，大小與位移一定正確，
   不像本地標頭可能把大小放在資料描述元裡）。 */

export const ZIP_METHOD = { store: 0, deflate: 8 } as const;

export interface ZipEntryInfo {
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  dataOffset: number;
}

function findEocd(view: DataView, bytes: Uint8Array): number {
  /* 中央目錄結束記錄固定 22 位元組，但可能有註解，所以從尾巴往回找。 */
  const min = Math.max(0, bytes.length - 66000);
  for (let i = bytes.length - 22; i >= min; i--) {
    if (view.getUint32(i, true) === 0x06054b50) return i;
  }
  throw new Error("這不是一個有效的 zip 檔（找不到中央目錄）");
}

export function listEntries(bytes: Uint8Array): ZipEntryInfo[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEocd(view, bytes);
  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries: ZipEntryInfo[] = [];
  const decoder = new TextDecoder();
  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== 0x02014b50) throw new Error("中央目錄損毀");
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLen));
    /* 本地標頭的檔名與額外欄位長度可能與中央目錄不同，必須從本地標頭重新算資料起點。 */
    if (view.getUint32(localOffset, true) !== 0x04034b50) throw new Error("本地標頭損毀：" + name);
    const localNameLen = view.getUint16(localOffset + 26, true);
    const localExtraLen = view.getUint16(localOffset + 28, true);
    entries.push({ name, method, compressedSize, uncompressedSize,
      dataOffset: localOffset + 30 + localNameLen + localExtraLen });
    offset += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  /* DecompressionStream 在某些環境吃的是 ReadableStream；用 Blob 轉一次最保險。 */
  const stream = new Blob([data as unknown as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function readZipEntries(bytes: Uint8Array, wanted: string[]): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  const targets = new Set(wanted);
  for (const entry of listEntries(bytes)) {
    if (!targets.has(entry.name)) continue;
    const raw = bytes.subarray(entry.dataOffset, entry.dataOffset + entry.compressedSize);
    if (entry.method === ZIP_METHOD.store) out.set(entry.name, raw.slice());
    else if (entry.method === ZIP_METHOD.deflate) out.set(entry.name, await inflateRaw(raw));
    else throw new Error("不支援的壓縮方式 " + entry.method + "（" + entry.name + "）");
  }
  return out;
}
